import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  UpgradeTagSize,
  UpgradeType,
} from '@freelancer/components/upgrade-tag';
import type { BackendUpdateErrorResponse } from '@freelancer/datastore';
import type {
  ProjectBookmarksCollection,
  SearchActiveContest,
  SearchActiveProject,
} from '@freelancer/datastore/collections';
import {
  Currency,
  ProjectBookmark,
  ProjectBookmarkType,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import type { AmountRange } from '@freelancer/pipes';
import { trackById } from '@freelancer/ui';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { RibbonPlacement } from '@freelancer/ui/ribbon';
import { TagColor, TagSize } from '@freelancer/ui/tag';
import {
  FontColor,
  FontWeight,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import { getConvertedRoundedAmount } from './project-contest-card.helper';

@Component({
  standalone: false,
  selector: 'fl-project-contest-card',
  template: `
    <div class="Container">
      <!-- Left column-->
      <div
        class="Header"
        [class.Header-freeBid]="showFreeBidIcon"
      >
        <div class="Title">
          <h2
            class="text-color-inherit text-body-20 Title-text {{
              isProject(cardDataInfo) ? 'mb-xxxsmall' : 'mb-small'
            }} {{
              isProject(cardDataInfo)
                ? 'desktop:mb-xxsmall'
                : 'desktop:mb-small'
            }}"
          >
            {{ cardDataInfo.title }}
          </h2>
          @if (showFreeBidIcon) {
            <fl-tag
              i18n-matTooltip="
                Tooltip indicating that bidding on the project is free
              "
              matTooltip="Bidding on this project is free from October 18 - October 22 to celebrate Diwali"
              class="Title-tooltip-label"
              i18n="'Text indicating that bidding is free'"
              [color]="TagColor.DARK_GREEN"
              [size]="TagSize.SMALL"
              [textTransform]="TextTransform.UPPERCASE"
              [fontWeight]="FontWeight.BOLD"
            >
              Free to bid
            </fl-tag>
          }
        </div>
        <div
          class="BudgetUpgradeWrapper {{
            isProject(cardDataInfo) ||
            (isContest(cardDataInfo) && upgrades.length > 0)
              ? 'mb-xxsmall'
              : 'mb-small'
          }}"
        >
          @if (showBudget && isProject(cardDataInfo)) {
            <div
              class="BudgetUpgradeWrapper-budget mr-small mb-xxsmall desktop:mb-xxxsmall"
            >
              <div
                class="text-foreground text-body-16 font-normal mr-xxxsmall"
                i18n="Shows the project budget"
              >
                Budget
                @if (!isConvertedBudget) {
                  {{
                    cardDataInfo.budget
                      | flCurrency
                        : cardDataInfo.currency.code
                        : { withPrecision: false }
                  }}
                  @if (cardDataInfo.type === ProjectTypeApi.HOURLY) {
                    <div class="Hourly">per hour</div>
                  }
                }

                @if (budgetRange && isConvertedBudget) {
                  @if (cardDataInfo.type === ProjectTypeApi.FIXED) {
                    <span
                      class="BudgetTooltip"
                      i18n-matTooltip="
                        Shows the project budget in the viewer's currency
                      "
                      matTooltip="~{{
                        budgetRange
                          | flCurrency
                            : viewerCurrency.code
                            : { withPrecision: false }
                      }}"
                    >
                      {{
                        cardDataInfo.budget
                          | flCurrency
                            : cardDataInfo.currency.code
                            : { withPrecision: false }
                      }}
                    </span>
                  }

                  @if (cardDataInfo.type === ProjectTypeApi.HOURLY) {
                    <span
                      class="BudgetTooltip"
                      i18n-matTooltip="
                        Shows the project's pay rate in the viewer's currency
                      "
                      matTooltip="~{{
                        budgetRange
                          | flCurrency
                            : viewerCurrency.code
                            : { withPrecision: false }
                      }} per hour"
                    >
                      {{
                        cardDataInfo.budget
                          | flCurrency
                            : cardDataInfo.currency.code
                            : { withPrecision: false }
                      }}
                      <div class="Hourly">per hour</div>
                    </span>
                  }
                }
              </div>
            </div>
          }

          <fl-project-contest-card-tags
            class="mb-xxsmall desktop:mb-xxxsmall"
            [upgrades]="upgrades"
            [isContest]="isContest(cardDataInfo)"
            [isProject]="isProject(cardDataInfo)"
          ></fl-project-contest-card-tags>
        </div>
      </div>
      <!-- END Left column-->

      <!-- right column-->
      <div
        class="FlexWrapper desktop:mb-none {{
          showRibbon &&
          ((isProject(cardDataInfo) && !isSealed) || isContest(cardDataInfo))
            ? 'mb-large'
            : 'mb-small'
        }}"
      >
        @if (hasCounter) {
          <div class="mr-small desktop:mr-mid">
            <div
              class="BidEntryData text-body-16 desktop:text-body-20 text-foreground font-normal"
            >
              <!-- Project Bids -->
              @if (showBudget && isProject(cardDataInfo)) {
                <div>
                  @if (cardDataInfo.bidStats.bidCount > 1) {
                    <ng-container
                      i18n="Indicates the number of bids the project has"
                    >
                      {{ cardDataInfo.bidStats.bidCount }} bids
                    </ng-container>
                  }
                  @if (cardDataInfo.bidStats.bidCount === 1) {
                    <ng-container
                      i18n="Indicates that there is one bid made on a project"
                    >
                      1 bid
                    </ng-container>
                  }
                </div>
              }
              <!-- Contest Entries -->
              @if (isContest(cardDataInfo)) {
                <div>
                  @if (cardDataInfo.entryCount > 1) {
                    <ng-container
                      i18n="Indicates the number of entries a contest has"
                    >
                      {{ cardDataInfo.entryCount }} entries
                    </ng-container>
                  }
                  @if (cardDataInfo.entryCount === 1) {
                    <ng-container i18n="Indicates that a contest has 1 entry">
                      {{ cardDataInfo.entryCount }} entry
                    </ng-container>
                  }
                </div>
              }
            </div>
          </div>
        }

        <div [class.AverageBidContainer]="showRibbon">
          @if (isProject(cardDataInfo)) {
            <ng-container *flFeature="Feature.SHOW_AVERAGE_BID">
              @if (
                showBudget && !isSealed && cardDataInfo.bidStats.bidCount !== 0
              ) {
                <div class="AverageBid">
                  <div class="AverageBid-container">
                    <div
                      i18n="Average bid"
                      class="AverageBid-amount text-body-16 desktop:text-body-20 font-bold desktop_small:text-right text-foreground desktop:mr-none mr-xxxsmall"
                    >
                      @if (!isConvertedBudget) {
                        {{
                          cardDataInfo.bidStats.bidAvg
                            | flCurrency
                              : cardDataInfo.currency.code
                              : { withPrecision: false }
                        }}
                      }
                      @if (
                        isConvertedBudget &&
                        equivalentAverageBidInViewerCurrency
                      ) {
                        <span
                          class="AverageBidContainer-tooltip mr-xxxsmall"
                          flTrackingLabel="AverageProjectBidTooltip"
                          matTooltip="~{{
                            equivalentAverageBidInViewerCurrency
                              | flCurrency
                                : viewerCurrency.code
                                : { withPrecision: false }
                          }}"
                        >
                          {{
                            cardDataInfo.bidStats.bidAvg
                              | flCurrency
                                : cardDataInfo.currency.code
                                : { withPrecision: false }
                          }}
                        </span>
                      }
                      @if (cardDataInfo.type === ProjectTypeApi.HOURLY) {
                        <div class="Hourly">per hour</div>
                      }
                    </div>
                  </div>
                  <p
                    class="text-body-12 AverageBid-copy desktop_small:text-right"
                    i18n="Average bid copy"
                  >
                    average bid
                  </p>
                </div>
              }
            </ng-container>
          }
          @if (isContest(cardDataInfo)) {
            <p
              class="text-body-16 ContestPrize desktop:text-body-20 desktop_small:text-right font-bold desktop:mr-none mr-xxxsmall"
              [class.desktop:show]="showRibbon"
            >
              {{
                cardDataInfo.prize
                  | flCurrency
                    : cardDataInfo.currency.code
                    : { withPrecision: false }
              }}
            </p>
          }
          @if (
            isProject(cardDataInfo) &&
            cardDataInfo.bidStats.bidCount === 0 &&
            !isSealed
          ) {
            <div class="Ribbon">
              <ng-container [ngTemplateOutlet]="ribbon"></ng-container>
            </div>
          }

          @if (isContest(cardDataInfo) && cardDataInfo.entryCount === 0) {
            <div class="Ribbon">
              <ng-container [ngTemplateOutlet]="ribbon"></ng-container>
              @if (isContest(cardDataInfo)) {
                <p
                  class="text-body-16 ContestPrizeLeft desktop:text-body-20 desktop_small:text-right font-bold desktop:mr-none mr-xxxsmall desktop:hide"
                >
                  {{
                    cardDataInfo.prize
                      | flCurrency
                        : cardDataInfo.currency.code
                        : { withPrecision: false }
                  }}
                </p>
              }
            </div>
          }

          <ng-template #ribbon>
            <fl-ribbon
              class="RibbonLeft desktop:mr-none {{
                isContest(cardDataInfo) ? 'mr-large' : 'mr-none'
              }}"
              [placement]="RibbonPlacement.LEFT"
              [placementDesktop]="RibbonPlacement.RIGHT"
            >
              @if (isProject(cardDataInfo)) {
                <ng-container i18n="First bid"> BE FIRST TO BID </ng-container>
              }
              @if (isContest(cardDataInfo)) {
                <ng-container i18n="First entry">
                  BE FIRST TO ENTER
                </ng-container>
              }
            </fl-ribbon>
          </ng-template>

          @if (isProject(cardDataInfo) && isSealed) {
            <fl-upgrade-tag
              class="Sealed"
              i18n-matTooltip="Tooltip message for Sealed project upgrade"
              matTooltip="Details of the bids placed in this project can only be seen by the client and their collaborators."
              [size]="UpgradeTagSize.SMALL"
              [upgradeType]="UpgradeType.SEALED"
            ></fl-upgrade-tag>
          }
        </div>
      </div>
      <!-- END right column-->
    </div>
    <!-- END Container-->

    <!-- Description and skills -->
    <p
      class="mb-xxsmall desktop:mb-xsmall"
      [class.whitespace-pre-line]="displayLineBreaks"
      [flMaxLines]="3"
      [flReadMore]="true"
      (expand)="handleExpand($event)"
    >
      {{ cardDataInfo.description }}
    </p>

    <div class="SkillsWrapper mb-xsmall desktop:hide">
      @for (
        skill of cardDataInfo.skills | slice: 0 : displayLimit;
        track trackById($index, skill)
      ) {
        <fl-tag
          class="SkillsWrapper-skill"
          [borderless]="true"
        >
          {{ skill.name }}
        </fl-tag>
      }

      @if (cardDataInfo.skills.length > displayLimit) {
        <span
          class="text-color-inherit text-body-16 MoreSkills"
          i18n="Number of projects's extra skills"
        >
          {{ cardDataInfo.skills.length - displayLimit }} more
        </span>
      }
    </div>

    <div class="SkillsWrapper mb-xsmall desktop:show">
      @for (
        skill of cardDataInfo.skills | slice: 0 : displayLimitDesktop;
        track trackById($index, skill)
      ) {
        <fl-tag
          class="SkillsWrapper-skill"
          [borderless]="true"
        >
          {{ skill.name }}
        </fl-tag>
      }

      @if (cardDataInfo.skills.length > displayLimitDesktop) {
        <span
          class="text-color-inherit text-body-16 MoreSkills"
          i18n="Number of projects's extra skills"
        >
          {{ cardDataInfo.skills.length - displayLimitDesktop }} more
        </span>
      }
    </div>

    <div class="Footer">
      <div
        class="Footer-wrapper {{
          isProject(cardDataInfo) && userHasBid ? 'mb-xxsmall' : 'mb-none'
        }}"
      >
        <div class="FlexWrapper mb-xxxsmall desktop:mb-none">
          <div
            matTooltip="Overall rating given to the client by previous freelancers."
            i18n-matTooltip="Tooltip message for client rating"
          >
            <fl-rating
              class="ClientRating mr-xxsmall"
              [readOnly]="true"
              [control]="ratingControl"
              [inline]="true"
            ></fl-rating>
          </div>

          <fl-review-count
            i18n-matTooltip="Tooltip message for client review count"
            matTooltip="Total number of reviews the client has received."
            [counter]="cardDataInfo.clientReputation?.reviews || 0"
          ></fl-review-count>
        </div>

        <div class="FlexWrapper mb-xxxsmall desktop:mb-none">
          <div class="UrgentProject">
            <!-- Time left indicator -->
            @if (hasSubmissionTimePassed === false) {
              <span
                class="text-foreground text-body-16 {{
                  isUrgent ? 'mr-xxsmall' : 'mr-none'
                }}"
                i18n="Remaining time"
              >
                <fl-relative-time
                  [color]="FontColor.INHERIT"
                  [continueAfterTimePassed]="false"
                  [date]="
                    isProject(cardDataInfo)
                      ? cardDataInfo.timeSubmitted
                      : (cardDataInfo?.timePosted ?? 0)
                  "
                  [size]="TextSize.INHERIT"
                  [strict]="true"
                  [suffix]="false"
                ></fl-relative-time>
                ago
              </span>
            }
            @if (hasSubmissionTimePassed) {
              @if (isProject(cardDataInfo)) {
                <p
                  i18n="Indicator that the project is closed for bidding"
                  class="{{ isUrgent ? 'mr-xxsmall' : 'mr-none' }}"
                >
                  Bidding period closed
                </p>
              }
              @if (isContest(cardDataInfo)) {
                <p
                  i18n="Indicator that the contest is closed for entries"
                  class="{{ isUrgent ? 'mr-xxsmall' : 'mr-none' }}"
                >
                  Entry period closed
                </p>
              }
            }

            <!-- Urgent upgrade -->
            @if (isUrgent) {
              <fl-upgrade-tag
                class="Urgent"
                i18n-matTooltip="Tooltip message for Urgent project upgrade"
                matTooltip="Bidding is only open for 24 hours."
                [size]="UpgradeTagSize.SMALL"
                [upgradeType]="UpgradeType.URGENT"
              ></fl-upgrade-tag>
            }
          </div>
          @if (bookmark) {
            <app-project-bookmark-button
              class="Bookmark"
              [colorSubscribed]="IconColor.SECONDARY"
              [project]="cardDataInfo"
              [type]="bookmarkType"
              [bookmark]="bookmark"
              (error)="bookmarkError.emit($event)"
              (success)="bookmarkSuccess.emit()"
            ></app-project-bookmark-button>
          }
        </div>
      </div>
      @if (isProject(cardDataInfo) && userHasBid) {
        <div class="Footer-section">
          <div class="BidIndicator">
            <fl-icon
              class="mr-xxxsmall"
              [name]="'ui-check-in-circle-v2'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
            <p
              class="text-body-16"
              i18n="Bid indicator text"
            >
              You have already bid on this project
            </p>
          </div>
        </div>
      }
    </div>
  `,
  styleUrls: ['./project-contest-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectContestCardComponent implements OnChanges, OnInit {
  Feature = Feature;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  ProjectTypeApi = ProjectTypeApi;
  RibbonPlacement = RibbonPlacement;
  TagColor = TagColor;
  TagSize = TagSize;
  TextSize = TextSize;
  TextTransform = TextTransform;
  trackById = trackById;
  UpgradeTagSize = UpgradeTagSize;
  UpgradeType = UpgradeType;

  @Input() cardDataInfo: SearchActiveContest | SearchActiveProject;
  @Input() isFreeBidEnabled = false;
  @Input() userHasBid?: boolean;
  @Input() viewerCurrency: Currency;
  @Input() bookmarkType: ProjectBookmarkType;
  @Input() bookmark?: ProjectBookmark;
  @Input() showBudget = true;

  @Output() bookmarkError = new EventEmitter<
    BackendUpdateErrorResponse<ProjectBookmarksCollection>
  >();
  @Output() bookmarkSuccess = new EventEmitter<void>();

  ratingControl: FormControl<number>;
  displayLineBreaks = false;
  equivalentAverageBidInViewerCurrency?: number;
  hasCounter = false;

  readonly displayLimit = 2;
  readonly displayLimitDesktop = 5;

  upgrades: any;
  isSealed: boolean;
  isUrgent: boolean;
  showRibbon: boolean;
  budgetRange: AmountRange;
  isConvertedBudget: boolean;
  hasSubmissionTimePassed?: boolean;
  showFreeBidIcon = false;

  handleExpand(isExpanded: boolean): void {
    this.displayLineBreaks = isExpanded;
  }

  ngOnInit(): void {
    if (this.cardDataInfo.upgradeNames.includes(UpgradeType.SEALED)) {
      this.isSealed = true;
    } else {
      this.isSealed = false;
    }

    if (this.cardDataInfo.upgradeNames.includes(UpgradeType.URGENT)) {
      this.isUrgent = true;
    } else {
      this.isUrgent = false;
    }

    this.ratingControl = new FormControl(
      this.cardDataInfo.clientReputation?.rating ?? 0,
      { nonNullable: true },
    );

    if (this.isProject(this.cardDataInfo)) {
      if (this.cardDataInfo.bidStats.bidCount === 0) {
        this.showRibbon = true;
      } else {
        this.showRibbon = false;
      }

      this.hasSubmissionTimePassed =
        this.cardDataInfo.bidTimeLeft <= Date.now();

      this.upgrades = this.cardDataInfo.upgradeNames.filter(
        upgrade =>
          upgrade !== UpgradeType.SEALED && upgrade !== UpgradeType.URGENT,
      );
    }

    if (this.isContest(this.cardDataInfo)) {
      if (this.cardDataInfo.entryCount === 0) {
        this.showRibbon = true;
      } else {
        this.showRibbon = false;
      }

      this.hasSubmissionTimePassed = isDefined(this.cardDataInfo.timeEnded)
        ? this.cardDataInfo.timeEnded <= Date.now()
        : undefined;

      this.upgrades = this.cardDataInfo.upgradeNames.filter(
        upgrade =>
          upgrade !== UpgradeType.URGENT &&
          upgrade !== UpgradeType.CUSTOM_JUDGING,
      );
    }

    this.hasCounter = this.isProject(this.cardDataInfo)
      ? this.cardDataInfo.bidStats.bidCount > 0
      : this.isContest(this.cardDataInfo)
        ? this.cardDataInfo.entryCount > 0
        : false;
  }

  ngOnChanges(): void {
    if (this.isProject(this.cardDataInfo)) {
      if (
        this.viewerCurrency?.exchangeRate &&
        this.cardDataInfo.currency.exchangeRate
      ) {
        this.budgetRange = {
          minimum: getConvertedRoundedAmount(
            this.cardDataInfo.budget.minimum,
            this.cardDataInfo.currency.exchangeRate,
            this.viewerCurrency.exchangeRate,
          ),
          maximum: this.cardDataInfo.budget.maximum
            ? getConvertedRoundedAmount(
                this.cardDataInfo.budget.maximum,
                this.cardDataInfo.currency.exchangeRate,
                this.viewerCurrency.exchangeRate,
              )
            : undefined,
        };

        this.isConvertedBudget =
          this.cardDataInfo.currency.id !== this.viewerCurrency.id;

        this.equivalentAverageBidInViewerCurrency = this.cardDataInfo.bidStats
          .bidAvg
          ? getConvertedRoundedAmount(
              this.cardDataInfo.bidStats.bidAvg,
              this.cardDataInfo.currency.exchangeRate,
              this.viewerCurrency.exchangeRate,
            )
          : undefined;
      }

      this.showFreeBidIcon =
        this.isFreeBidEnabled &&
        !this.cardDataInfo.enterpriseIds?.length &&
        !this.cardDataInfo.isInsourceProject;
    }
  }

  isProject(
    cardDataInfo: SearchActiveContest | SearchActiveProject,
  ): cardDataInfo is SearchActiveProject {
    return (
      'budget' in cardDataInfo &&
      'bidStats' in cardDataInfo &&
      'bidPeriod' in cardDataInfo &&
      'bidTimeLeft' in cardDataInfo
    );
  }

  isContest(
    cardDataInfo: SearchActiveContest | SearchActiveProject,
  ): cardDataInfo is SearchActiveContest {
    return (
      'prize' in cardDataInfo &&
      'timePosted' in cardDataInfo &&
      'entryCount' in cardDataInfo
    );
  }
}
